<script setup>
/* eslint-disable vue/no-bare-strings-in-template */
import { computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { toast } from 'dashboard/helper/toast';
import { useMapGetter } from 'dashboard/composables/store';
import { useWhatsAppCallPermission } from 'dashboard/composables/useWhatsAppCallPermission';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const props = defineProps({
  phone: { type: String, default: '' },
  inboxId: { type: [Number, String], default: null },
});

const { t } = useI18n();
const store = useStore();

// Shared permission flow: state machine + ActionCable watcher + timeout.
// state: idle | checking | requesting | waiting | permitted | denied
const { state, requestCall, reset, isProcessing } = useWhatsAppCallPermission();

const trunks = computed(() => store.getters['whatsappSipTrunks/getRecords']);

// O botão só aparece se a inbox DESTA conversa tiver um trunk SIP — sem trunk
// dessa inbox não há número pra ligar por ele. Sem inbox também oculta; quem
// quiser liga pelo dialer escolhendo o número manualmente.
const inboxHasTrunk = computed(
  () =>
    props.inboxId != null &&
    trunks.value.some(trunk => Number(trunk.inbox_id) === Number(props.inboxId))
);

// Guards pra evitar o GET /whatsapp_sip/sip_trunks em situacoes onde ele e
// garantido falhar e travar o re-render do ChatList:
//   1. Feature flag da conta — 95% das contas nao tem o feature pago.
//   2. Agente sem whatsapp_sip_extension cadastrado — mesmo com feature
//      ativo, sem ramal o fetch da erro e o botao nao funcionaria.
const currentAccountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const isWhatsAppSipEnabled = computed(() =>
  isFeatureEnabledonAccount.value(
    currentAccountId.value,
    FEATURE_FLAGS.WHATSAPP_SIP_TELEPHONY
  )
);
const hasWhatsAppSipExtension = computed(
  () => !!currentUser.value?.whatsapp_sip_extension
);
const canFetchTrunks = computed(
  () => isWhatsAppSipEnabled.value && hasWhatsAppSipExtension.value
);

onMounted(() => {
  if (!canFetchTrunks.value) return;
  if (trunks.value.length === 0) {
    // Agentes recebem 401 (policy admin-only). O botão simplesmente não renderiza —
    // o feature whatsapp-sip calling é admin-only por design.
    store.dispatch('whatsappSipTrunks/fetch').catch(() => {});
  }
});

// Normalize phone to wa_id format (strip +, will be normalized server-side)
const waId = computed(() => props.phone.replace(/\D/g, ''));

// Permission is scoped by number + inbox. When either changes (e.g. agent
// navigates to another conversation/inbox), reset the sticky state so the next
// click re-checks instead of reusing a stale `permitted` state.
watch([() => props.phone, () => props.inboxId], () => {
  reset();
});

const dial = () => {
  emitter.emit(BUS_EVENTS.WHATSAPP_SIP_SHOW_DIALER, {
    number: props.phone,
    autoCall: true,
    inboxId: props.inboxId,
  });
};

const handleClick = () => {
  if (!props.phone) {
    toast.error(t('WHATSAPP_SIP_CALL.PERMISSION.NO_PHONE'));
    return;
  }

  requestCall({
    waId: waId.value,
    inboxId: props.inboxId,
    onPermitted: dial,
  });
};

const tooltip = computed(() => {
  switch (state.value) {
    case 'checking':
      return t('WHATSAPP_SIP_CALL.TOOLTIP.CHECKING');
    case 'requesting':
      return t('WHATSAPP_SIP_CALL.TOOLTIP.REQUESTING');
    case 'waiting':
      return t('WHATSAPP_SIP_CALL.TOOLTIP.WAITING');
    case 'denied':
      return t('WHATSAPP_SIP_CALL.TOOLTIP.DENIED');
    default:
      return t('WHATSAPP_SIP_CALL.TOOLTIP.CALL', { phone: props.phone });
  }
});

const dotClass = computed(() => {
  switch (state.value) {
    case 'checking':
    case 'requesting':
      return 'bg-n-amber-9 animate-pulse';
    case 'waiting':
      return 'bg-n-amber-9 animate-pulse';
    case 'permitted':
      return 'bg-green-600';
    case 'denied':
      return 'bg-n-ruby-9';
    default:
      return 'bg-green-600';
  }
});
</script>

<template>
  <span v-if="inboxHasTrunk" class="contents">
    <button
      v-tooltip.top-end="tooltip"
      type="button"
      class="relative inline-flex items-center justify-center rounded-lg h-8 w-8 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-n-slate-8"
      :class="{
        'cursor-wait': isProcessing,
        'cursor-pointer': !isProcessing,
      }"
      @click="handleClick"
    >
      <!-- Background -->
      <span
        class="absolute inset-0 rounded-lg transition-colors"
        :class="{
          'bg-n-amber-3 dark:bg-n-amber-9/20': state === 'waiting',
          'bg-n-alpha-1 hover:bg-n-alpha-2': state !== 'waiting',
        }"
      />

      <!-- Status indicator dot -->
      <span
        class="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-n-solid-1 z-10"
        :class="dotClass"
      />

      <!-- Spinner when processing -->
      <template v-if="isProcessing">
        <svg
          class="relative z-10 h-4 w-4 animate-spin text-n-amber-11"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          />
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </template>

      <!-- WhatsApp icon -->
      <template v-else>
        <svg
          class="relative z-10 h-4 w-4"
          :class="{
            'text-n-amber-11': state === 'waiting',
            'text-n-ruby-11': state === 'denied',
            'text-green-600': state !== 'waiting' && state !== 'denied',
          }"
          viewBox="0 0 24 24"
          fill="currentColor"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"
          />
        </svg>
      </template>
    </button>
  </span>
</template>
